import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { dataService } from "../../../Services/data/data.service";
import { flowBoxButtonAction } from "../../../Config/constants";
import { useFlowBoxSetupLog } from "../../../hooks/useFlowBoxSetupLog";
import IconButton from "../../Shared/Buttons/IconButton";
import Label from "../../Shared/Label";
import Chip from "../../Shared/ui/Chip";

const formatTime = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  if (isNaN(d)) return "-";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const ACTION_LABELS = {
  [flowBoxButtonAction.MEASURE]: "measure",
  [flowBoxButtonAction.START]: "start",
  [flowBoxButtonAction.STOP]: "stop",
  [flowBoxButtonAction.TOGGLE]: "toggle",
  [flowBoxButtonAction.SUBTRACT]: "subtract",
};

const COLLAPSED_LIMIT = 10;

export default function FlowBoxSetupLogView({ deviceId }) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(() => {
    if (!deviceId) return;
    setLoading(true);
    dataService.getFlowBoxSetupLog(deviceId)
      .then((data) => setEntries(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [deviceId]);

  useEffect(() => { load(); }, [load]);

  useFlowBoxSetupLog(deviceId, (entry) => {
    setEntries((prev) => [entry, ...prev]);
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Label>{t("flowBox.setup.logTitle")}</Label>
        <IconButton
          size="small"
          ariaLabel={t("flowBox.setup.refresh")}
          onClick={load}
          disabled={loading}
        >
          <RefreshCw size={14} style={loading ? { animation: "spin 0.8s linear infinite" } : undefined} />
        </IconButton>
      </div>

      <p style={{ margin: 0, fontSize: 13, color: "#6b7280", fontFamily: "Lato, sans-serif", lineHeight: 1.5 }}>
        {t("flowBox.setup.logHint")}
      </p>

      {entries.length === 0 && !loading && (
        <div style={{
          textAlign: "center",
          padding: "28px 16px",
          color: "#9ca3af",
          fontSize: 13,
          fontFamily: "Lato, sans-serif",
          background: "#f9fafb",
          borderRadius: 8,
          border: "1px dashed #e5e7eb",
          marginTop: 4,
        }}>
          {t("flowBox.setup.logEmpty")}
        </div>
      )}

      {entries.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 0, marginTop: 4 }}>
          {/* Header */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "56px 64px 1fr auto",
            gap: "0 12px",
            padding: "6px 10px",
            background: "#f3f4f6",
            borderRadius: "6px 6px 0 0",
            borderBottom: "1px solid #e5e7eb",
          }}>
            {["flowBox.setup.colTime", "flowBox.setup.colButton", "flowBox.setup.colEndpoint", "flowBox.setup.colStatus"].map((k) => (
              <span key={k} style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", fontFamily: "Lato, sans-serif", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                {t(k)}
              </span>
            ))}
          </div>

          {(expanded ? entries : entries.slice(0, COLLAPSED_LIMIT)).map((entry, idx, arr) => (
            <div
              key={entry.id}
              style={{
                display: "grid",
                gridTemplateColumns: "56px 64px 1fr auto",
                gap: "0 12px",
                padding: "8px 10px",
                alignItems: "center",
                background: idx % 2 === 0 ? "#ffffff" : "#fafafa",
                borderBottom: "1px solid #f3f4f6",
                borderLeft: "1px solid #e5e7eb",
                borderRight: "1px solid #e5e7eb",
                ...(idx === arr.length - 1 && (expanded || entries.length <= COLLAPSED_LIMIT)
                  ? { borderRadius: "0 0 6px 6px", borderBottom: "1px solid #e5e7eb" }
                  : {}),
              }}
            >
              <span style={{ fontSize: 12, fontFamily: "monospace", color: "#374151" }}>
                {formatTime(entry.timestampServer)}
              </span>

              <span style={{ fontSize: 13, fontWeight: 600, fontFamily: "Lato, sans-serif", color: "#374151" }}>
                {t("flowBox.setup.buttonN", { n: entry.buttonIndex })}
              </span>

              <span style={{ fontSize: 13, fontFamily: "Lato, sans-serif", color: entry.hadMapping ? "#374151" : "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {entry.hadMapping
                  ? `${entry.externalApiName || t("flowBox.configure.unknownEndpoint")}${entry.apiActionId != null ? ` - ${ACTION_LABELS[entry.apiActionId] || entry.apiActionId}` : ""}`
                  : t("flowBox.setup.noEndpoint")}
              </span>

              <Chip
                variant={entry.hadMapping ? "success" : "warning"}
                size="extraSmall"
              >
                {entry.hadMapping ? t("flowBox.setup.mapped") : t("flowBox.setup.unmapped")}
              </Chip>
            </div>
          ))}

          {entries.length > COLLAPSED_LIMIT && (
            <button
              onClick={() => setExpanded((v) => !v)}
              style={{
                display: "block",
                width: "100%",
                padding: "6px 10px",
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderTop: "none",
                borderRadius: "0 0 6px 6px",
                fontSize: 12,
                color: "#2563eb",
                fontFamily: "Lato, sans-serif",
                cursor: "pointer",
                textAlign: "center",
              }}
            >
              {expanded
                ? t("flowBox.setup.showLess")
                : t("flowBox.setup.showMore", { count: entries.length - COLLAPSED_LIMIT })}
            </button>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
